import { Card, Table, Input, Button, DatePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";

import Loader from "../loader/loader";
import PageTitle from "../page-header/PageHeader";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";
import { loadAllAssignedTask } from "../../redux/rtk/features/projectManagement/project/assignedTask/assignedTask";


function GetAllAssignedTasks() {
  const [columnsToShow, setColumnsToShow] = useState([]);
  const { list = [], loading } = useSelector((state) => state?.AssignedTask || {});
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({});
  const searchInput = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAllAssignedTask());
  }, [dispatch]);

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  // Apply filters whenever they change or when data loads
  useEffect(() => {
    if (!list || !list.length) {
      setFilteredData([]);
      return;
    }

    let result = [...list];

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;

      switch (key) {
        case "UserName":
          result = result.filter((item) => {
            const fullName = `${item?.assignedUserFirstName || ""} ${
              item?.assignedUserLastName || ""
            }`.toLowerCase().trim();
            return fullName.includes(value.toLowerCase());
          });
          break;
        case "projectName":
          result = result.filter((item) => {
            const projectName = item?.projectName?.toLowerCase() || "";
            return projectName.includes(value.toLowerCase());
          });
          break;
        case "milestoneName":
          result = result.filter((item) => {
            const milestoneName = item?.milestoneName?.toLowerCase() || "";
            return milestoneName.includes(value.toLowerCase());
          });
          break;
        case "taskName":
          result = result.filter((item) => {
            const taskName = item?.taskName?.toLowerCase() || "";
            return taskName.includes(value.toLowerCase());
          });
          break;
        case "taskStartDate":
          result = result.filter((item) => {
            if (!item.taskStartDate) return false;
            const dateValue = dayjs(item.taskStartDate).format("YYYY-MM-DD");
            return dateValue === value;
          });
          break;
        case "taskEndDate":
          result = result.filter((item) => {
            if (!item.taskEndDate) return false;
            const dateValue = dayjs(item.taskEndDate).format("YYYY-MM-DD");
            return dateValue === value;
          });
          break;
        case "taskStatus":
          result = result.filter((item) => {
            const status = item?.taskStatus?.toLowerCase() || "";
            return status.includes(value.toLowerCase());
          });
          break;
        case "priority":
          result = result.filter((item) => {
            const priority = item?.priority?.toLowerCase() || "";
            return priority.includes(value.toLowerCase());
          });
          break;
        default:
          break;
      }
    });

    setFilteredData(result);
  }, [list, filters]);

  const handleSearch = (value, dataIndex) => {
    setFilters((prev) => ({
      ...prev,
      [dataIndex]: value,
    }));
  };

  const handleReset = (dataIndex) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeholder || `Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedKeys(value ? [value] : []);
            handleSearch(value, dataIndex);
          }}
          onPressEnter={(e) => e.stopPropagation()}
          style={{ marginBottom: 8, display: "block" }}
          autoFocus
        />
        <Button
          onClick={() => {
            clearFilters && clearFilters();
            handleReset(dataIndex);
          }}
          size="small"
          style={{ width: "100%" }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    onFilter: () => true,
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const getDateColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <DatePicker
          style={{ marginBottom: 8, width: "100%" }}
          value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
          onChange={(date, dateString) => {
            setSelectedKeys(dateString ? [dateString] : []);
            handleSearch(dateString, dataIndex);
          }}
        />
        <Button
          onClick={() => {
            clearFilters && clearFilters();
            handleReset(dataIndex);
          }}
          size="small"
          style={{ width: "100%" }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: () => true,
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const columns = [
    {
      id: 1,
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
    },
    {
      id: 2,
      title: "User Name",
      dataIndex: "UserName",
      key: "UserName",
      ...getColumnSearchProps("UserName", "Search by name"),
      render: (_, record) => {
        const fullName = `${record?.assignedUserFirstName || ""} ${
          record?.assignedUserLastName || ""
        }`.trim();
        return fullName || "--";
      },
    },
    {
      id: 3,
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      ...getColumnSearchProps("projectName", "Search project"),
      render: (projectName) => projectName || "--",
    },
    {
      id: 4,
      title: "Milestone",
      dataIndex: "milestoneName",
      key: "milestoneName",
      ...getColumnSearchProps("milestoneName", "Search milestone"),
      render: (milestoneName) => milestoneName || "--",
    },
    {
      id: 5,
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      ...getColumnSearchProps("priority", "Search priority"),
      render: (priority) => priority || "--",
    },
    {
      id: 6,
      title: "Task",
      dataIndex: "taskName",
      key: "taskName",
      ...getColumnSearchProps("taskName", "Search task name"),
      render: (taskName) => taskName || "--",
    },
    {
      id: 7,
      title: "Start Date",
      dataIndex: "taskStartDate",
      key: "taskStartDate",
      ...getDateColumnSearchProps("taskStartDate", "Search start date"),
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "--"),
    },
    {
      id: 8,
      title: "End Date",
      dataIndex: "taskEndDate",
      key: "taskEndDate",
      ...getDateColumnSearchProps("taskEndDate", "Search end date"),
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "--"),
    },
    {
      id: 9,
      title: "Task Status",
      dataIndex: "taskStatus",
      key: "taskStatus",
      ...getColumnSearchProps("taskStatus", "Search status"),
      render: (taskStatus) => taskStatus || "--",
    },
    {
      id: 10,
      title: "Completion Time (hrs)",
      dataIndex: "completionTime",
      key: "completionTime",
      render: (completionTime) =>
        completionTime !== null && completionTime !== undefined
          ? completionTime
          : "--",
    },
  ];

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  const addKeys = (arr) =>
    arr.map((i, index) => ({ ...i, key: i.id, serial: index + 1 }));

  // Prepare data for CSV export
  const csvData = list.map((item) => ({
    Project: item?.projectName || "--",
    "Project Description": item?.projectDescription || "--",
    "Project Start Date": item?.projectStartDate
      ? dayjs(item.projectStartDate).format("YYYY-MM-DD")
      : "--",
    "Project End Date": item?.projectEndDate
      ? dayjs(item.projectEndDate).format("YYYY-MM-DD")
      : "--",
    "Project Status": item?.projectStatus || "--",
    Milestone: item?.milestoneName || "--",
    "Milestone Description": item?.milestoneDescription || "--",
    "Milestone Start Date": item?.milestoneStartDate
      ? dayjs(item.milestoneStartDate).format("YYYY-MM-DD")
      : "--",
    "Milestone End Date": item?.milestoneEndDate
      ? dayjs(item.milestoneEndDate).format("YYYY-MM-DD")
      : "--",
    "Milestone Status": item?.milestoneStatus || "--",
    Task: item?.taskName || "--",
    "Task Description": item?.taskDescription || "--",
    "Task Start Date": item?.taskStartDate
      ? dayjs(item.taskStartDate).format("YYYY-MM-DD")
      : "--",
    "Task End Date": item?.taskEndDate
      ? dayjs(item.taskEndDate).format("YYYY-MM-DD")
      : "--",
    "Task Status": item?.taskStatus || "--",
    Priority: item?.priority || "--",
    "Completion Time (hrs)": item?.completionTime ?? "--",
    "Assigned User": `${item?.assignedUserFirstName || ""} ${
      item?.assignedUserLastName || ""
    }`.trim() || "--",
  }));

  // Determine which data to show (filtered or all)
  const dataToShow = Object.keys(filters).length > 0 ? filteredData : list;

  return (
    <>
      <PageTitle title="Back" />
      {!loading ? (
        <Card className="mt-5">
          <div className="text-center my-2 flex justify-between">
            <h5 className="department-list-title text-color-2 text-xl mb-2">
              All Assigned Tasks
            </h5>
            {list?.length > 0 && (
              <div>
                <CsvLinkBtn>
                  <CSVLink
                    data={csvData}
                    className="btn btn-dark btn-sm mb-1"
                    filename="assigned_tasks.csv"
                  >
                    Download CSV
                  </CSVLink>
                </CsvLinkBtn>
              </div>
            )}
          </div>

          {list && (
            <div style={{ marginBottom: "30px" }}>
              <ColVisibilityDropdown
                options={columns}
                columns={columns}
                columnsToShowHandler={columnsToShowHandler}
              />
            </div>
          )}

          <Table
            scroll={{ x: true }}
            loading={loading}
            columns={columnsToShow}
            dataSource={dataToShow ? addKeys(dataToShow) : []}
          />
        </Card>
      ) : (
        <Loader />
      )}
    </>
  );
}

export default GetAllAssignedTasks;